import _isEqual from 'lodash/isEqual';
import { ReactNode, useEffect, useState } from 'react';
import { useProjectPermissionsQuery } from 'queries/project';
import { useUser } from './use-user';

export type Operation = 'CREATE' | 'READ' | 'WRITE' | 'DELETE';

export type ProjectResource =
  | 'company'
  | 'document'
  | 'member'
  | 'milestone'
  | 'phase'
  | 'project'
  | 'space'
  | 'statusUpdate'
  | 'task'
  | 'fields';

export type ResourceType =
  | 'all'
  | 'member'
  | 'field'
  | 'user'
  | 'task'
  | 'project'
  | 'phase'
  | 'statusUpdate'
  | 'space'
  | 'company'
  | 'milestone'
  | 'spaceTab'
  | 'document'
  | 'conversation'
  | 'comment'
  | 'folder'
  | 'baseline'
  | 'template'
  | 'report'
  | 'team'
  | 'projects'
  | 'projectFields'
  | 'taskFields'
  | 'fields'
  | 'costRate'
  | 'rateCard';

export interface GranularPermissions {
  canRead?: boolean;
  canCreate?: boolean;
  canDelete?: boolean;
  canWrite?: boolean;
}
export interface HasAccessProps {
  permissions?: Operation[];
  resource?: ResourceType;
  resourceId?: number;
  resourceModule?: ProjectResource;
  resourceModuleId?: number; // needed for nested objects like fields
  disabled?: boolean;
  newProjectPermissionsApi?: boolean; // adding this temporarily; can be removed once all project permissions will be consumed from new API
  fallback?: ReactNode;
}

interface ReturnProps {
  hasAccess: boolean;
  isLoading?: boolean;
}

const broadenResourcePermissions = (operation: Operation): Operation[] =>
  operation != null
    ? operation === 'WRITE'
      ? ['READ', 'WRITE', 'CREATE', 'DELETE']
      : [operation]
    : [];

export const useHasAccess = ({
  permissions = ['READ'],
  resource = 'all',
  resourceId,
  resourceModule,
  resourceModuleId,
  newProjectPermissionsApi = false,
}: HasAccessProps): ReturnProps => {
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const user = useUser();
  const { data: projectPermissions, isLoading: isProjectPermissionsLoading } =
    useProjectPermissionsQuery(resourceId ?? 0, {
      enabled:
        newProjectPermissionsApi &&
        resource === 'projects' &&
        resourceId != null,
    });

  useEffect(() => {
    let resourcePermissions = user?.permissions[resource];

    if (resource === 'all') {
      setHasAccess(true);
    } else if (
      ['projectFields', 'taskFields', 'fields'].includes(resource) &&
      resourceId
    ) {
      let resourceData = user?.permissions[resource];

      resourceData = resourceData?.find?.((r) => r?.fieldId === resourceId);
      resourcePermissions = broadenResourcePermissions(
        resourceData?.permission,
      );
      setHasAccess(permissions.every((op) => resourcePermissions.includes(op)));
    } else if (resource === 'projects' && resourceId && resourceModule) {
      if (newProjectPermissionsApi && projectPermissions != null) {
        resourcePermissions = projectPermissions?.[resourceModule] ?? []; // this is for props other than fields
        if (['fields'].includes(resourceModule) && resourceModuleId != null) {
          const resourceData = resourcePermissions.find?.(
            (r) => r?.fieldId === resourceModuleId,
          );

          resourcePermissions = broadenResourcePermissions(
            resourceData?.permission,
          );
        }
      } else {
        const resourceData = user?.permissions?.projectPermissionBeanMap;

        resourcePermissions =
          resourceData?.[resourceId]?.[resourceModule] ?? [];
      }

      setHasAccess(permissions.every((op) => resourcePermissions.includes(op)));
    } else if (resourcePermissions?.length > 0) {
      setHasAccess(permissions.every((op) => resourcePermissions.includes(op)));
    } else {
      setHasAccess(false);
    }
  }, [
    newProjectPermissionsApi,
    permissions,
    projectPermissions,
    resource,
    resourceId,
    resourceModule,
    resourceModuleId,
    user,
  ]);

  return { hasAccess, isLoading: isProjectPermissionsLoading };
};
